import { webRTCAtom } from "@/Components/Board/boardAtoms";
import { Exercise } from "@/Components/Exercises/exerciseSchema";
import { ClientExercise } from "@/types/exercise";
import { currentIdentity } from "@/utils/identity";
import Storage from "@/utils/Storage";
import axios from "axios";
import { atom } from "jotai";
import {
    shareGoalsBoardAtom,
    showChatAtom,
    showGoalsAtom,
    videoDataAtom,
} from "./videoAtoms";

export enum LiveExercisePhase {
    Preview = "preview",
    Shared = "shared",
}

export type LiveExerciseState = {
    exercise: Exercise;
    clientExercise: ClientExercise | null;
    phase: LiveExercisePhase;
    /**
     * Whether the exercise is currently displayed on the client's screen. The
     * coach can toggle this without ending the session — the client's panel is
     * only hidden, so the exercise stays mounted and keeps syncing.
     */
    isVisibleToClient: boolean;
};

export const liveExerciseAtom = atom<LiveExerciseState | null>(null);
export const showLiveExerciseStartModalAtom = atom(false);
export const isLiveExerciseFullscreenAtom = atom(true);
export const isSharingLiveExerciseAtom = atom(false);
/**
 * Tracks the coach's slide position while previewing a brand new exercise, so
 * the shared session can start from the same slide once the ClientExercise
 * exists. Resumed exercises don't need this because their position is already
 * stored under their clientExerciseId.
 */
export const previewIndexesVisitedAtom = atom<number[]>([0]);

export const liveExercisePanelVisibleAtom = atom((get) => {
    const liveExercise = get(liveExerciseAtom);
    if (!liveExercise || get(showChatAtom) || get(showGoalsAtom)) {
        return false;
    }
    if (currentIdentity().user?.type === "client") {
        return liveExercise.isVisibleToClient;
    }
    return true;
});

export const openLiveExerciseFlowAtom = atom(null, (get, set) => {
    if (get(showGoalsAtom)) {
        set(showGoalsAtom, false);
        set(shareGoalsBoardAtom, false);
    }
    set(showChatAtom, false);
    if (get(liveExerciseAtom)) {
        return;
    }
    set(showLiveExerciseStartModalAtom, true);
});

export const startLiveExercisePreviewAtom = atom(
    null,
    (
        get,
        set,
        exercise: Exercise,
        clientExercise: ClientExercise | null = null,
    ) => {
        set(showLiveExerciseStartModalAtom, false);
        set(showChatAtom, false);
        set(previewIndexesVisitedAtom, [0]);
        set(liveExerciseAtom, {
            exercise,
            clientExercise,
            phase: LiveExercisePhase.Preview,
            isVisibleToClient: false,
        });
    },
);

export const startLiveSharingAtom = atom(null, async (get, set) => {
    const liveExercise = get(liveExerciseAtom);
    const videoData = get(videoDataAtom);
    if (!liveExercise || !videoData || get(isSharingLiveExerciseAtom)) {
        return;
    }

    if (liveExercise.phase === LiveExercisePhase.Shared) {
        if (liveExercise.clientExercise && !liveExercise.isVisibleToClient) {
            set(liveExerciseAtom, { ...liveExercise, isVisibleToClient: true });
            get(webRTCAtom).sendMessage({
                type: "setLiveExercise",
                clientExerciseId: liveExercise.clientExercise.id,
                isVisible: true,
            });
        }
        return;
    }

    set(isSharingLiveExerciseAtom, true);
    try {
        let clientExercise = liveExercise.clientExercise;
        if (!clientExercise) {
            const { data } = await axios.post<ClientExercise>(
                route("client-exercises.store"),
                {
                    client_id: videoData.appointment.client.id,
                    exercise_id: liveExercise.exercise.id,
                    appointment_id: videoData.appointment.id,
                },
            );
            clientExercise = data;
            Storage.save(
                `exerciseIndexesVisited-${liveExercise.exercise.id}-${clientExercise.id}`,
                get(previewIndexesVisitedAtom),
            );
        }
        if (get(liveExerciseAtom) !== liveExercise) {
            // The preview was closed or replaced while the request was in
            // flight, so don't resurrect it or open it on the client's screen.
            return;
        }
        set(liveExerciseAtom, {
            ...liveExercise,
            clientExercise,
            phase: LiveExercisePhase.Shared,
            isVisibleToClient: true,
        });
        get(webRTCAtom).sendMessage({
            type: "setLiveExercise",
            clientExerciseId: clientExercise.id,
            isVisible: true,
        });
    } finally {
        set(isSharingLiveExerciseAtom, false);
    }
});

export const stopLiveSharingAtom = atom(null, (get, set) => {
    const liveExercise = get(liveExerciseAtom);
    if (
        liveExercise?.phase !== LiveExercisePhase.Shared ||
        !liveExercise.clientExercise ||
        !liveExercise.isVisibleToClient
    ) {
        return;
    }
    set(liveExerciseAtom, { ...liveExercise, isVisibleToClient: false });
    get(webRTCAtom).sendMessage({
        type: "setLiveExercise",
        clientExerciseId: liveExercise.clientExercise.id,
        isVisible: false,
    });
});

export const endLiveExerciseAtom = atom(null, (get, set) => {
    const liveExercise = get(liveExerciseAtom);
    if (liveExercise?.phase === LiveExercisePhase.Shared) {
        get(webRTCAtom).sendMessage({
            type: "setLiveExercise",
            clientExerciseId: null,
            isVisible: false,
        });
    }
    set(liveExerciseAtom, null);
});

export const closeLiveExercisePanelLocallyAtom = atom(null, (get, set) => {
    set(liveExerciseAtom, null);
});

/**
 * Counts received setLiveExercise commands so that a fetch still in flight
 * when a newer command arrives (e.g. the coach ends the exercise right after
 * sharing it) is discarded instead of mounting stale state.
 */
const liveExerciseCommandCounterAtom = atom(0);

export const receiveLiveExerciseCommandAtom = atom(
    null,
    async (get, set, clientExerciseId: number | null, isVisible: boolean) => {
        const commandNumber = get(liveExerciseCommandCounterAtom) + 1;
        set(liveExerciseCommandCounterAtom, commandNumber);
        if (clientExerciseId === null) {
            set(liveExerciseAtom, null);
            return;
        }
        const currentLiveExercise = get(liveExerciseAtom);
        if (currentLiveExercise?.clientExercise?.id === clientExerciseId) {
            set(liveExerciseAtom, {
                ...currentLiveExercise,
                isVisibleToClient: isVisible,
            });
            if (isVisible) {
                set(showChatAtom, false);
            }
            return;
        }
        const { data } = await axios.get<{
            exercise: Exercise;
            clientExercise: ClientExercise;
        }>(
            route("client-exercises.show.data", {
                client_exercise: clientExerciseId,
            }),
        );
        if (get(liveExerciseCommandCounterAtom) !== commandNumber) {
            return;
        }
        if (isVisible) {
            set(showChatAtom, false);
        }
        set(liveExerciseAtom, {
            exercise: data.exercise,
            clientExercise: data.clientExercise,
            phase: LiveExercisePhase.Shared,
            isVisibleToClient: isVisible,
        });
    },
);

/**
 * When the other participant's data channel (re)connects, the coach re-sends
 * the active shared exercise so a client who refreshed mid-call is brought
 * back into it.
 */
export const resendLiveExerciseStateAtom = atom(null, (get) => {
    const liveExercise = get(liveExerciseAtom);
    if (
        currentIdentity().user?.type === "coach" &&
        liveExercise?.phase === LiveExercisePhase.Shared &&
        liveExercise.clientExercise
    ) {
        get(webRTCAtom).sendMessage({
            type: "setLiveExercise",
            clientExerciseId: liveExercise.clientExercise.id,
            isVisible: liveExercise.isVisibleToClient,
        });
    }
});

/**
 * The exercise is coach-controlled, so when the coach's connection goes away
 * the client's panel closes. The coach keeps their own panel open and the
 * reconnect re-send restores the client when the connection returns.
 */
export const handleLiveExercisePeerLeftAtom = atom(null, (get, set) => {
    if (currentIdentity().user?.type === "client") {
        set(liveExerciseAtom, null);
    }
});

export const resetLiveExerciseStateAtom = atom(null, (get, set) => {
    set(liveExerciseAtom, null);
    set(showLiveExerciseStartModalAtom, false);
    set(isSharingLiveExerciseAtom, false);
    set(previewIndexesVisitedAtom, [0]);
});
